import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert a title to a URL-friendly slug
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Extract short ID from a slug (last segment if it looks like a short ID)
 * Short IDs are 8 characters, alphanumeric
 */
export function extractShortId(slug: string): string | null {
  const parts = slug.split('-');
  const lastPart = parts[parts.length - 1];
  // Check if last part is a short ID (8 alphanumeric characters)
  if (lastPart && /^[a-z0-9]{8}$/i.test(lastPart)) {
    return lastPart;
  }
  return null;
}

/**
 * Create a short ID from a full UUID (first 8 characters, no hyphens)
 */
export function createShortId(id: string): string {
  return id.replace(/-/g, '').substring(0, 8);
}

/**
 * Create a session URL slug from title
 * If id is provided and hasDuplicates is true, include a short ID suffix
 * Format: title-slug or title-slug-shortid
 */
export function createSessionSlug(title: string, id?: string, hasDuplicates: boolean = false): string {
  const baseSlug = slugify(title);
  // Only add ID suffix if there are duplicates and an ID is provided
  if (hasDuplicates && id) {
    const shortId = createShortId(id);
    return `${baseSlug}-${shortId}`;
  }
  return baseSlug;
}

/**
 * Convert a slug back to a topic (for querying)
 * Note: This is a best-effort conversion since slugs remove special characters
 */
export function unslugify(slug: string): string {
  // Remove short ID suffix if present
  const slugWithoutId = slug.replace(/-[a-z0-9]{8}$/i, '');
  return slugWithoutId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Version-related constants and utilities
 */
export const APP_VERSION_STORAGE_KEY = "lyder-talk-app-version";

/**
 * Get the stored app version from localStorage
 * Returns null if not found or if localStorage is unavailable/disabled
 */
export function getStoredVersion(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return localStorage.getItem(APP_VERSION_STORAGE_KEY);
  } catch (error) {
    // Handle localStorage errors gracefully (disabled, quota exceeded, etc.)
    if (error instanceof DOMException) {
      console.warn("Failed to read version from localStorage:", error.message);
    } else {
      console.warn("Failed to read version from localStorage:", error);
    }
    return null;
  }
}

/**
 * Store the current app version in localStorage
 * Handles errors gracefully (quota exceeded, disabled localStorage, etc.)
 */
export function storeVersion(version: string): void {
  if (typeof window === "undefined") return;

  try {
    localStorage.setItem(APP_VERSION_STORAGE_KEY, version);
  } catch (error) {
    // Handle localStorage errors gracefully
    // Common errors: quota exceeded, localStorage disabled
    if (error instanceof DOMException) {
      console.warn("Failed to store version in localStorage:", error.message);
    } else {
      console.warn("Failed to store version in localStorage:", error);
    }
    // Continue execution - version check will still work, just won't persist
  }
}

/**
 * Cache for the fetched version to avoid repeated network requests
 */
let versionCache: string | null = null;
let versionFetchPromise: Promise<string> | null = null;

/**
 * Get the current app version from /version.json
 * Fetches the version at runtime and caches it for the session
 * Falls back to stored version or "0.0.0" if fetch fails
 */
export async function getCurrentVersion(): Promise<string> {
  // Return cached version if available
  if (versionCache !== null) {
    return versionCache;
  }

  // If a fetch is already in progress, return that promise
  if (versionFetchPromise !== null) {
    return versionFetchPromise;
  }

  // Start fetching version
  versionFetchPromise = (async () => {
    try {
      const response = await fetch("/version.json", {
        cache: "no-store", // Always fetch fresh version
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch version.json: ${response.status}`);
      }

      const data = await response.json();
      const version = data?.version || "0.0.0";

      // Cache the version
      versionCache = version;
      return version;
    } catch (error) {
      // Fallback to stored version if available
      const storedVersion = getStoredVersion();
      if (storedVersion) {
        versionCache = storedVersion;
        return storedVersion;
      }

      // Final fallback
      console.warn("Failed to fetch version from /version.json, using fallback:", error);
      versionCache = "0.0.0";
      return "0.0.0";
    } finally {
      // Clear the promise so we can retry if needed
      versionFetchPromise = null;
    }
  })();

  return versionFetchPromise;
}

/**
 * Parse a semantic version string into parts
 * Handles formats like "1.2.3", "0.0.1-alpha", "1.2", etc.
 * Returns [major, minor, patch] or null if invalid
 */
function parseVersion(version: string): [number, number, number] | null {
  if (!version || typeof version !== "string") {
    return null;
  }

  // Remove pre-release identifiers (e.g., "0.0.1-alpha" -> "0.0.1")
  const baseVersion = version.split("-")[0].trim();

  // Split by dots
  const parts = baseVersion.split(".").map(part => {
    // Remove any non-numeric characters and parse
    const num = parseInt(part.replace(/[^0-9]/g, ""), 10);
    return isNaN(num) ? 0 : num;
  });

  // Ensure we have at least 3 parts (major, minor, patch)
  while (parts.length < 3) {
    parts.push(0);
  }

  // Return only first 3 parts (major, minor, patch)
  return [parts[0], parts[1], parts[2]];
}

/**
 * Compare two semantic version strings
 * Returns:
 * - -1 if version1 < version2
 * - 0 if version1 === version2
 * - 1 if version1 > version2
 * 
 * Falls back to string comparison if versions are not in semantic format
 */
export function compareVersions(version1: string, version2: string): number {
  if (!version1 || !version2) {
    // Fallback to string comparison if either is missing
    return version1 === version2 ? 0 : (version1 < version2 ? -1 : 1);
  }

  const parsed1 = parseVersion(version1);
  const parsed2 = parseVersion(version2);

  // If either version can't be parsed, fall back to string comparison
  if (!parsed1 || !parsed2) {
    return version1 === version2 ? 0 : (version1 < version2 ? -1 : 1);
  }

  const [major1, minor1, patch1] = parsed1;
  const [major2, minor2, patch2] = parsed2;

  // Compare major version
  if (major1 !== major2) {
    return major1 < major2 ? -1 : 1;
  }

  // Compare minor version
  if (minor1 !== minor2) {
    return minor1 < minor2 ? -1 : 1;
  }

  // Compare patch version
  if (patch1 !== patch2) {
    return patch1 < patch2 ? -1 : 1;
  }

  // Versions are equal
  return 0;
}

/**
 * Generate a UUID v4
 * Fallback for non-secure contexts where crypto.randomUUID is not available
 */
export function generateUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // Fallback for non-secure context (HTTP)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
